import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

import { smsShareUrl, whatsappShareUrl } from '../links'

interface ShareSheetProps {
  title: string
  url: string
  inviteOnly: boolean
  onClose: () => void
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'

/**
 * Accessible share fallback used when the Web Share API is unavailable.
 * Invite links keep the token; the URL stays selectable even when clipboard
 * access is denied.
 */
export function ShareSheet({
  title,
  url,
  inviteOnly,
  onClose,
}: ShareSheetProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const text = `${title}\n${url}`

  useEffect(() => {
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!focusable || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [onClose])

  async function copyLink() {
    setCopyError(false)
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard')
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
      setCopyError(true)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-deep/50 p-0 min-[600px]:items-center min-[600px]:p-6">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="activity-share-title"
        className="max-h-[90dvh] w-full max-w-[460px] overflow-y-auto rounded-t-card bg-paper p-5 pb-[calc(1.25rem+var(--safe-bottom))] shadow-float min-[600px]:rounded-card"
      >
        <div className="flex items-center justify-between gap-3">
          <h2
            id="activity-share-title"
            className="font-display text-xl font-extrabold text-deep"
          >
            {t('activityDetail.actions.shareTitle')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-ghost size-11 justify-center rounded-full p-0"
            aria-label={t('activityDetail.actions.close')}
            onClick={onClose}
          >
            <KuniriIcons.x size={22} weight={kuniriIconWeight} aria-hidden="true" />
          </button>
        </div>

        {inviteOnly ? (
          <p className="mt-2 text-sm leading-relaxed text-muted">
            {t('activityDetail.actions.shareInviteHint')}
          </p>
        ) : null}

        <p className="mt-4 rounded-md border border-line bg-white p-3 text-sm leading-relaxed break-all text-deep select-all">
          {url}
        </p>

        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            className="btn-secondary w-full bg-white"
            onClick={() => void copyLink()}
          >
            <KuniriIcons.link
              size={20}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.actions.shareCopy')}
          </button>
          <a
            href={whatsappShareUrl(text)}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary w-full bg-white"
          >
            <KuniriIcons.chatCircle
              size={20}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.actions.shareWhatsApp')}
          </a>
          <a href={smsShareUrl(text)} className="btn-secondary w-full bg-white">
            <KuniriIcons.chat
              size={20}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.actions.shareSms')}
          </a>
        </div>

        {copied ? (
          <p
            role="status"
            className="mt-3 text-sm font-semibold text-teal"
          >
            {t('activityDetail.actions.shareCopied')}
          </p>
        ) : null}
        {copyError ? (
          <p role="alert" className="mt-3 text-sm font-semibold text-danger">
            {t('activityDetail.actions.shareCopyError')}
          </p>
        ) : null}
      </div>
    </div>
  )
}
